import { Box, Typography } from '@mui/material'
import {
  PasswordInput,
  SimpleForm,
  TextInput,
  Toolbar,
  SaveButton
} from 'react-admin'
import { FieldValues, useWatch } from 'react-hook-form'
import useAccount from '../hooks/useAccount'
import { useCurrentPartnerInfo } from '../hooks/useCurrentPartnerInfo'
import { getUser } from '../repository/getUser'
import { isAdmin, isVendor } from '@/authProvider'
import { useState } from 'react'
import PasswordStrengthIndicator from '@/components/PasswordStrength'
import { PasswordState } from '../types/PasswordState'
import { VendorAccountInfo } from './VendorAccountInfo'
import { AddressInfoForm } from './AddressInfoForm'

const PasswordFields = () => {
  const password = useWatch({ name: 'password' }) || ''

  return (
    <>
      <Box display="flex">
        <Box mr="20px">
          <PasswordInput
            source="password"
            inputProps={{ 'data-testid': 'password' }}
          />
        </Box>
        <PasswordInput
          source="confirmPassword"
          label="Confirm Password"
          inputProps={{ 'data-testid': 'confirm-password' }}
        />
      </Box>
      <PasswordStrengthIndicator password={password} />
    </>
  )
}

const GeneralInfo = () => {
  const { updateEmail, updateInfo, updatePassword } = useAccount()
  const { partner } = useCurrentPartnerInfo()
  const { email: userEmail } = getUser()

  // This state is used to clear the password form after the submit
  const [password, setPassword] = useState<PasswordState>({
    changed: false
  })

  const submitInfoUpdate = (values: FieldValues) => {
    updateInfo({
      primaryContact: values.primaryContact,
      phoneNumber: values.phoneNumber
    })
  }

  const submitEmailUpdate = (values: FieldValues) => {
    updateEmail(values.userEmail)
  }

  const submitPasswordUpdate = (values: FieldValues) => {
    updatePassword({
      password: values.password,
      confirmPassword: values.confirmPassword
    })

    setPassword({
      password: '',
      confirmPassword: '',
      changed: !password.changed
    })
  }

  const SaveOnlyToolbar = () => (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  )

  return (
    <Box>
      {isVendor() ? (
        partner && <VendorAccountInfo partner={partner} />
      ) : (
        <SimpleForm
          data-testid="update-info-form"
          onSubmit={submitInfoUpdate}
          toolbar={<SaveOnlyToolbar />}
        >
          <Typography variant="h6" gutterBottom>
            General Information
          </Typography>
          <Box display="flex">
            <Box mr="20px">
              <TextInput
                type="email"
                source="primaryContact"
                label="Primary Contact"
                sx={{ width: 300 }}
                inputProps={{ 'data-testid': 'primary-contact' }}
                defaultValue={partner?.primaryContact}
              />
            </Box>
            <TextInput
              source="phoneNumber"
              label="Phone Number"
              inputProps={{ 'data-testid': 'phone-number' }}
              defaultValue={partner?.phoneNumber}
            />
          </Box>
        </SimpleForm>
      )}
      {partner && <AddressInfoForm partner={partner} />}
      {!isAdmin() && (
        <SimpleForm
          data-testid="update-email-form"
          onSubmit={submitEmailUpdate}
          toolbar={<SaveOnlyToolbar />}
        >
          <Typography variant="h6" gutterBottom>
            Update Login Email
          </Typography>
          <TextInput
            type="email"
            source="userEmail"
            label="User Email"
            sx={{ width: 300 }}
            inputProps={{ 'data-testid': 'user-email' }}
            defaultValue={userEmail}
          />
        </SimpleForm>
      )}
      {!isAdmin() && (
        <SimpleForm
          data-testid="update-password-form"
          onSubmit={submitPasswordUpdate}
          defaultValues={password}
          toolbar={<SaveOnlyToolbar />}
        >
          <Typography variant="h6" gutterBottom>
            Update Login Password
          </Typography>
          <PasswordFields />
        </SimpleForm>
      )}
    </Box>
  )
}
export default GeneralInfo
